import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { StudioRuntimeSettings } from './studioApi';
import { updateRuntimeGroup } from './studioRuntimeSettings';

export const validPreCallUrl = (value: string) => {
  if (
    !value ||
    value.length > 2048 ||
    /[\s\p{Cc}]/u.test(value) ||
    value.includes('#') ||
    value.includes('\\') ||
    !/^https?:\/\/[^/?#]/i.test(value)
  )
    return false;
  try {
    const url = new URL(value);
    return (
      ['http:', 'https:'].includes(url.protocol) &&
      !!url.hostname &&
      !url.username &&
      !url.password &&
      !/^https?:\/\/[^/?#]*@/i.test(value)
    );
  } catch {
    return false;
  }
};
export const preCallSettingsError = (
  value: StudioRuntimeSettings | null | undefined,
  inbound = false,
) => {
  const preCall = value?.preCall;
  if (!preCall) return '';
  if (inbound && preCall.enabled)
    return 'Enabled pre-call lookup is not supported for inbound SIP campaigns.';
  if (preCall.url !== undefined && !validPreCallUrl(preCall.url))
    return 'Use a static HTTP(S) URL of at most 2,048 characters, without user information, whitespace, control characters or a fragment.';
  if (preCall.enabled && preCall.url === undefined)
    return 'Enter a pre-call URL before enabling this lookup.';
  return '';
};
export const StudioPreCallSettings = ({
  value,
  onChange,
  disabled,
  squad = false,
  inbound = false,
}: {
  value: StudioRuntimeSettings | null | undefined;
  onChange: (value: StudioRuntimeSettings | null) => void;
  disabled: boolean;
  squad?: boolean;
  inbound?: boolean;
}) => {
  const current = value?.preCall,
    locked = disabled || squad,
    issue = preCallSettingsError(value, inbound);
  return (
    <Stack gap={2}>
      <Typography variant="h6">Pre-call lookup</Typography>
      <Typography variant="body2" color="text.secondary">
        Optional reference context before conversation startup. Save and publish the branch
        separately; these controls do not send a request.
      </Typography>
      {squad && (
        <Alert severity="warning">
          Pre-call overrides require handoff-aware runtime support before use with a squad. Saved
          values remain visible.
        </Alert>
      )}
      <Button
        variant="outlined"
        disabled={disabled || current === undefined}
        sx={{ alignSelf: 'start' }}
        onClick={() => {
          if (!disabled) onChange(updateRuntimeGroup(value, 'preCall', undefined));
        }}>
        Clear pre-call override
      </Button>
      <TextField
        select
        label="Pre-call lookup policy"
        value={current === undefined ? 'native' : current.enabled ? 'on' : 'off'}
        disabled={locked}
        onChange={(event) => {
          if (locked || (inbound && event.target.value === 'on')) return;
          onChange(
            updateRuntimeGroup(
              value,
              'preCall',
              event.target.value === 'native'
                ? undefined
                : { ...current, enabled: event.target.value === 'on' },
            ),
          );
        }}>
        <MenuItem value="native">Use native policy (off)</MenuItem>
        <MenuItem value="on" disabled={inbound}>
          On
        </MenuItem>
        <MenuItem value="off">Off</MenuItem>
      </TextField>
      {inbound && (
        <Alert severity="info">
          Inbound SIP startup does not support private pre-call context. Use native/off for this
          campaign. The server also checks other campaigns consuming this agent.
        </Alert>
      )}
      <TextField
        label="Pre-call URL"
        value={current?.url ?? ''}
        disabled={locked || current === undefined}
        error={!!issue}
        helperText="Static HTTP(S), maximum 2,048 characters; no user information or fragment. No variable interpolation. Only public destinations can be used."
        slotProps={{ htmlInput: { maxLength: 2048 } }}
        onChange={(event) => {
          if (locked || !current) return;
          const next = { ...current };
          if (event.target.value === '') delete next.url;
          else next.url = event.target.value;
          onChange(updateRuntimeGroup(value, 'preCall', next));
        }}
      />
      <Typography variant="body2" color="text.secondary">
        The server makes a read-only GET with a maximum three-second deadline, no redirects and a
        technical X-Callevate-Call-Id header; no customer data or authorization is added. The
        response must be exactly a JSON object with a nonempty context string of at most 16 KiB of
        UTF-8 text. Lookup failures let the call continue without this context. Normal call
        permissions and admission checks still apply. Returned context is reference data; Job and
        business rules always take precedence.
      </Typography>
      {issue && <Alert severity="warning">{issue}</Alert>}
    </Stack>
  );
};
